import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  BaseRecord,
  useNavigation,
  useTable,
  useTranslate,
} from '@refinedev/core';
import { Button, Card, Flex, Spin, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useRulesContext } from 'contexts/rulesContext/RulesContext';
import apiEndpoint from 'core/common/apiEndPoints';
import replacePipe from 'core/utils/replacePipe';
import resourceName from 'core/common/resourceName';
import Loading from 'components/loading/loading';
import RuleChangePlanStrip from 'pages/ruleChange/rulePlan/RulePlanStrip';
import RuleChangeGroupAccordion from 'pages/ruleChange/ruleGroup/RuleGroupAccordion';
import regex from 'core/constants/Regex';

const buildFilterQuery = (appliedFilters: any) => {
  let query = '';
  if (appliedFilters?.name) {
    query += `&rule_name=${appliedFilters.name}`;
  }
  if (appliedFilters?.status?.length === 1) {
    const isActive = appliedFilters.status[0] === 'Active';
    query += `&rule_is_active=${isActive ? 'True' : 'False'}`;
  }
  if (appliedFilters?.dynamic) {
    Object.entries(appliedFilters.dynamic).forEach(([, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((val) => {
          const cleanedVal =
            typeof val === 'string'
              ? val.replace(regex.whitespaceCheck, '')
              : val;
          query += `&rule_filter=${cleanedVal}`;
        });
      }
    });
  }
  return query;
};

const NewRulePlanView = ({ pageType }) => {
  const { Title } = Typography;
  const { list } = useNavigation();
  const t = useTranslate();
  const { id } = useParams();
  const { rulePlanDataObj, setRulePlanDataObj, refetchRuleDetails } =
    useRulesContext();
  const [rulesDetails, setRuleDetails] = useState<any>();
  const [appliedFilters, setAppliedFilters] = useState<any>({});

  let rulesResourceId = id;
  if (rulePlanDataObj?.unique_id) {
    if (pageType === 'Show' && rulePlanDataObj?.view_unique_id) {
      rulesResourceId = rulePlanDataObj.view_unique_id;
    } else {
      rulesResourceId =
        rulePlanDataObj?.edit_unique_id || rulePlanDataObj?.unique_id;
    }
  }
  const filterQuery = buildFilterQuery(appliedFilters);
  const resource = useMemo(() => {
    const usePublished = pageType === 'Show' && rulePlanDataObj?.view_unique_id;
    const base = usePublished
      ? `${apiEndpoint.rulesPlan}/${rulesResourceId}`
      : `${apiEndpoint.rulesPlanDraft}/${rulesResourceId}`;
    return filterQuery ? `${base}/?${filterQuery.slice(1)}` : `${base}`;
  }, [pageType, rulePlanDataObj?.view_unique_id, rulesResourceId, filterQuery]);
  const { tableQueryResult } = useTable({
    resource,
    queryOptions: { enabled: !!rulesResourceId },
    pagination: { mode: 'off' },
    successNotification: false,
    errorNotification: (data: BaseRecord) => ({
      message: t('notifications.mutationError'),
      description:
        replacePipe(data?.message) || t('notifications.errorMessage'),
      type: 'error',
    }),
  });
  const { refetch: ruleDetailsRefetch, isFetching: isRuleListRefetching } =
    tableQueryResult;
  const rawResult = (tableQueryResult?.data?.data as BaseRecord)?.results || {};
  const hasSetPlan = useRef(false);
  useEffect(() => {
    if (
      !hasSetPlan.current &&
      !rulePlanDataObj?.unique_id &&
      rawResult?.unique_id
    ) {
      hasSetPlan.current = true;
      setRulePlanDataObj(rawResult);
    }
  }, [rawResult, rulePlanDataObj?.unique_id, setRulePlanDataObj]);

  useEffect(() => {
    if (
      rawResult?.unique_id &&
      JSON.stringify(rawResult) !== JSON.stringify(rulesDetails)
    ) {
      setRuleDetails(rawResult);
    }
  }, [rawResult, rulesDetails]);
  useEffect(() => {
    if (rulesResourceId) {
      ruleDetailsRefetch().catch(() => {});
    }
  }, [refetchRuleDetails]);
  const stableRefetch = useCallback(async () => {
    if (rulesResourceId) {
      await ruleDetailsRefetch().catch(() => {});
    }
  }, [ruleDetailsRefetch, rulesResourceId]);

  function goToList() {
    list(resourceName.rulePlans);
  }

  if (pageType !== 'Create' && !rulesDetails?.unique_id) return <Loading />;
  return (
    <>
      <Flex align="center" className="py-1">
        <Button
          type="text"
          className="mr-2"
          icon={<ArrowLeftOutlined />}
          onClick={goToList}
        />
        <Title className="text-white !my-3" level={4}>
          {t('rules.ruleDetails')}
        </Title>
      </Flex>
      <Card styles={{ body: { padding: 0 } }}>
        <RuleChangePlanStrip pageType={pageType} refetch={stableRefetch} />
        <Spin spinning={isRuleListRefetching}>
          {rulePlanDataObj?.unique_id ? (
            <RuleChangeGroupAccordion
              pageType={pageType}
              rulesDetails={rulesDetails}
              refetch={stableRefetch}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          ) : null}
        </Spin>
      </Card>
    </>
  );
};

export default NewRulePlanView;
